import { useState, useEffect, useRef, useCallback } from "react";
import { V } from "../theme.js";
import { OptBtn, AdjRow, Modal } from "../ui/components.jsx";

const DEFAULT_SHADOW  = { on:false, color:"rgba(0,0,0,0.5)", blur:8, ox:4, oy:4 };
const DEFAULT_BEVEL   = { on:false, size:2, opacity:0.5, highlightColor:"rgba(255,255,255,0.4)", shadowColor:"rgba(0,0,0,0.3)" };
const DEFAULT_OUTLINE = { on:false, color:"#f87171", size:2, opacity:1 };
// Phase 7
const DEFAULT_INNER_SHADOW = { on:false, color:"rgba(0,0,0,0.6)", blur:6, ox:2, oy:2, opacity:0.7 };
const DEFAULT_OUTER_GLOW   = { on:false, color:"#fbbf24", size:10, spread:0, opacity:0.6 };
const DEFAULT_INNER_GLOW   = { on:false, color:"#ffffff", size:8, opacity:0.5 };
const DEFAULT_COLOR_OVERLAY = { on:false, color:"#f87171", opacity:0.5, blend:"source-over" };
const DEFAULT_GRAD_OVERLAY  = { on:false, color1:"#000000", color2:"#ffffff", angle:0, opacity:0.5 };

/**
 * FxDialog — layer effects (drop shadow, bevel, outline).
 *
 * Props:
 *   open        boolean
 *   layerName   string
 *   layerIdx    number
 *   engRef      React ref to the engine (null in worker mode — live preview disabled)
 *   onCommit    (layerIdx, fx) => void
 *   onClose     () => void
 *   forceUpdate () => void   — trigger rAF re-render after live preview
 */
export function FxDialog({ open, layerName, layerIdx, engRef, onCommit, onClose, forceUpdate }) {
  const [shadow,  setShadow]  = useState(DEFAULT_SHADOW);
  const [bevel,   setBevel]   = useState(DEFAULT_BEVEL);
  const [outline, setOutline] = useState(DEFAULT_OUTLINE);
  const [innerShadow, setInnerShadow] = useState(DEFAULT_INNER_SHADOW);
  const [outerGlow, setOuterGlow] = useState(DEFAULT_OUTER_GLOW);
  const [innerGlow, setInnerGlow] = useState(DEFAULT_INNER_GLOW);
  const [colorOverlay, setColorOverlay] = useState(DEFAULT_COLOR_OVERLAY);
  const [gradOverlay, setGradOverlay] = useState(DEFAULT_GRAD_OVERLAY);
  const origFxRef = useRef(null);

  // Initialise state from the layer's current fx when dialog opens
  useEffect(() => {
    if (!open) { origFxRef.current = null; return; }
    const eng = engRef?.current;
    if (!eng) return;
    const L = eng.s.layers[layerIdx];
    if (!L) return;
    origFxRef.current = JSON.parse(JSON.stringify(L.fx));
    const fx = L.fx || {};
    setShadow(fx.shadow  ? { on:true,  ...fx.shadow  } : DEFAULT_SHADOW);
    setBevel( fx.bevel   ? { on:true,  ...fx.bevel   } : DEFAULT_BEVEL);
    setOutline(fx.outline? { on:true,  ...fx.outline } : DEFAULT_OUTLINE);
    setInnerShadow(fx.innerShadow ? { on:true, ...fx.innerShadow } : DEFAULT_INNER_SHADOW);
    setOuterGlow(fx.outerGlow ? { on:true, ...fx.outerGlow } : DEFAULT_OUTER_GLOW);
    setInnerGlow(fx.innerGlow ? { on:true, ...fx.innerGlow } : DEFAULT_INNER_GLOW);
    setColorOverlay(fx.colorOverlay ? { on:true, ...fx.colorOverlay } : DEFAULT_COLOR_OVERLAY);
    setGradOverlay(fx.gradOverlay ? { on:true, ...fx.gradOverlay } : DEFAULT_GRAD_OVERLAY);
  }, [open, layerIdx, engRef]);

  // Live preview
  useEffect(() => {
    if (!open) return;
    const eng = engRef?.current;
    if (!eng) return;
    const L = eng.s.layers[layerIdx];
    if (!L) return;
    L.fx = {
      shadow:  shadow.on  ? { color:shadow.color, blur:shadow.blur, ox:shadow.ox, oy:shadow.oy } : null,
      bevel:   bevel.on   ? { size:bevel.size, opacity:bevel.opacity } : null,
      outline: outline.on ? { color:outline.color, size:outline.size, opacity:outline.opacity } : null,
      innerShadow: innerShadow.on ? { color:innerShadow.color, blur:innerShadow.blur, ox:innerShadow.ox, oy:innerShadow.oy, opacity:innerShadow.opacity } : null,
      outerGlow: outerGlow.on ? { color:outerGlow.color, size:outerGlow.size, spread:outerGlow.spread, opacity:outerGlow.opacity } : null,
      innerGlow: innerGlow.on ? { color:innerGlow.color, size:innerGlow.size, opacity:innerGlow.opacity } : null,
      colorOverlay: colorOverlay.on ? { color:colorOverlay.color, opacity:colorOverlay.opacity, blend:colorOverlay.blend } : null,
      gradOverlay: gradOverlay.on ? { color1:gradOverlay.color1, color2:gradOverlay.color2, angle:gradOverlay.angle, opacity:gradOverlay.opacity } : null,
    };
    eng.s.dirty = true;
    forceUpdate?.();
  }, [open, layerIdx, shadow, bevel, outline, innerShadow, outerGlow, innerGlow, colorOverlay, gradOverlay, engRef, forceUpdate]);

  const handleCommit = useCallback(() => {
    onCommit(layerIdx, {
      shadow:  shadow.on  ? shadow  : null,
      bevel:   bevel.on   ? bevel   : null,
      outline: outline.on ? outline : null,
      innerShadow: innerShadow.on ? innerShadow : null,
      outerGlow: outerGlow.on ? outerGlow : null,
      innerGlow: innerGlow.on ? innerGlow : null,
      colorOverlay: colorOverlay.on ? colorOverlay : null,
      gradOverlay: gradOverlay.on ? gradOverlay : null,
    });
    origFxRef.current = null;
    onClose();
  }, [layerIdx, shadow, bevel, outline, innerShadow, outerGlow, innerGlow, colorOverlay, gradOverlay, onCommit, onClose]);

  const handleCancel = useCallback(() => {
    const eng = engRef?.current;
    if (origFxRef.current && eng?.s?.layers?.[layerIdx]) {
      eng.s.layers[layerIdx].fx = { ...origFxRef.current };
      eng.s.dirty = true;
    }
    origFxRef.current = null;
    onClose();
    forceUpdate?.();
  }, [layerIdx, engRef, onClose, forceUpdate]);

  if (!open) return null;

  return (
    <Modal onClose={handleCancel}>
      <div style={{ maxHeight:"calc(100vh - 120px)", overflowY:"auto" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:12 }}>
        Layer Effects — {layerName}
      </div>

      {/* Drop Shadow */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={shadow.on}
            onChange={e => setShadow(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Drop Shadow</span>
        </label>
        {shadow.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Blur"     value={shadow.blur} min={0}   max={50} onChange={v => setShadow(s => ({ ...s, blur:v }))} fmt={v => v+"px"}/>
            <AdjRow label="Offset X" value={shadow.ox}   min={-50} max={50} onChange={v => setShadow(s => ({ ...s, ox:v }))}   fmt={v => v+"px"}/>
            <AdjRow label="Offset Y" value={shadow.oy}   min={-50} max={50} onChange={v => setShadow(s => ({ ...s, oy:v }))}   fmt={v => v+"px"}/>
          </div>
        )}
      </div>

      {/* Bevel & Emboss */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={bevel.on}
            onChange={e => setBevel(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Bevel &amp; Emboss</span>
        </label>
        {bevel.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Size"    value={bevel.size}                    min={1} max={10}  onChange={v => setBevel(s => ({ ...s, size:v }))}         fmt={v => v+"px"}/>
            <AdjRow label="Opacity" value={Math.round(bevel.opacity*100)} min={0} max={100} onChange={v => setBevel(s => ({ ...s, opacity:v/100 }))}  fmt={v => v+"%"}/>
          </div>
        )}
      </div>

      {/* Outline */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={outline.on}
            onChange={e => setOutline(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Outline / Stroke</span>
        </label>
        {outline.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Size"    value={outline.size}                    min={1} max={20}  onChange={v => setOutline(s => ({ ...s, size:v }))}         fmt={v => v+"px"}/>
            <AdjRow label="Opacity" value={Math.round(outline.opacity*100)} min={0} max={100} onChange={v => setOutline(s => ({ ...s, opacity:v/100 }))}  fmt={v => v+"%"}/>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:10, color:V.muted }}>Color:</span>
              <input type="color" value={outline.color}
                onChange={e => setOutline(s => ({ ...s, color: e.target.value }))}
                style={{ width:28, height:20, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer", padding:0 }}/>
            </div>
          </div>
        )}
      </div>

      {/* Inner Shadow (Phase 7) */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={innerShadow.on}
            onChange={e => setInnerShadow(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Inner Shadow</span>
        </label>
        {innerShadow.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Blur"     value={innerShadow.blur}                     min={0}   max={50} onChange={v => setInnerShadow(s => ({ ...s, blur:v }))} fmt={v => v+"px"}/>
            <AdjRow label="Offset X" value={innerShadow.ox}                       min={-30} max={30} onChange={v => setInnerShadow(s => ({ ...s, ox:v }))}   fmt={v => v+"px"}/>
            <AdjRow label="Offset Y" value={innerShadow.oy}                       min={-30} max={30} onChange={v => setInnerShadow(s => ({ ...s, oy:v }))}   fmt={v => v+"px"}/>
            <AdjRow label="Opacity"  value={Math.round(innerShadow.opacity*100)}   min={0}   max={100} onChange={v => setInnerShadow(s => ({ ...s, opacity:v/100 }))} fmt={v => v+"%"}/>
          </div>
        )}
      </div>

      {/* Outer Glow (Phase 7) */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={outerGlow.on}
            onChange={e => setOuterGlow(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Outer Glow</span>
        </label>
        {outerGlow.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Size"    value={outerGlow.size}                    min={1}  max={50}  onChange={v => setOuterGlow(s => ({ ...s, size:v }))}    fmt={v => v+"px"}/>
            <AdjRow label="Spread"  value={outerGlow.spread}                  min={0}  max={20}  onChange={v => setOuterGlow(s => ({ ...s, spread:v }))}  fmt={v => v+"px"}/>
            <AdjRow label="Opacity" value={Math.round(outerGlow.opacity*100)} min={0}  max={100} onChange={v => setOuterGlow(s => ({ ...s, opacity:v/100 }))} fmt={v => v+"%"}/>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:10, color:V.muted }}>Color:</span>
              <input type="color" value={outerGlow.color}
                onChange={e => setOuterGlow(s => ({ ...s, color: e.target.value }))}
                style={{ width:28, height:20, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer", padding:0 }}/>
            </div>
          </div>
        )}
      </div>

      {/* Inner Glow (Phase 7) */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={innerGlow.on}
            onChange={e => setInnerGlow(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Inner Glow</span>
        </label>
        {innerGlow.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Size"    value={innerGlow.size}                    min={1}  max={40}  onChange={v => setInnerGlow(s => ({ ...s, size:v }))}    fmt={v => v+"px"}/>
            <AdjRow label="Opacity" value={Math.round(innerGlow.opacity*100)} min={0}  max={100} onChange={v => setInnerGlow(s => ({ ...s, opacity:v/100 }))} fmt={v => v+"%"}/>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:10, color:V.muted }}>Color:</span>
              <input type="color" value={innerGlow.color}
                onChange={e => setInnerGlow(s => ({ ...s, color: e.target.value }))}
                style={{ width:28, height:20, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer", padding:0 }}/>
            </div>
          </div>
        )}
      </div>

      {/* Color Overlay (Phase 7) */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={colorOverlay.on}
            onChange={e => setColorOverlay(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Color Overlay</span>
        </label>
        {colorOverlay.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Opacity" value={Math.round(colorOverlay.opacity*100)} min={0} max={100} onChange={v => setColorOverlay(s => ({ ...s, opacity:v/100 }))} fmt={v => v+"%"}/>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:10, color:V.muted }}>Color:</span>
              <input type="color" value={colorOverlay.color}
                onChange={e => setColorOverlay(s => ({ ...s, color: e.target.value }))}
                style={{ width:28, height:20, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer", padding:0 }}/>
            </div>
          </div>
        )}
      </div>

      {/* Gradient Overlay (Phase 7) */}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#aaa", marginBottom:6, cursor:"pointer" }}>
          <input type="checkbox" checked={gradOverlay.on}
            onChange={e => setGradOverlay(s => ({ ...s, on: e.target.checked }))}
            style={{ accentColor: V.accent }}/>
          <span style={{ fontWeight:600 }}>Gradient Overlay</span>
        </label>
        {gradOverlay.on && (
          <div style={{ paddingLeft:20, display:"flex", flexDirection:"column", gap:4 }}>
            <AdjRow label="Angle"   value={gradOverlay.angle}                    min={0}  max={360} onChange={v => setGradOverlay(s => ({ ...s, angle:v }))}   fmt={v => v+"°"}/>
            <AdjRow label="Opacity" value={Math.round(gradOverlay.opacity*100)}  min={0}  max={100} onChange={v => setGradOverlay(s => ({ ...s, opacity:v/100 }))} fmt={v => v+"%"}/>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:10, color:V.muted }}>From:</span>
              <input type="color" value={gradOverlay.color1} onChange={e => setGradOverlay(s => ({ ...s, color1: e.target.value }))}
                style={{ width:28, height:20, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer", padding:0 }}/>
              <span style={{ fontSize:10, color:V.muted }}>To:</span>
              <input type="color" value={gradOverlay.color2} onChange={e => setGradOverlay(s => ({ ...s, color2: e.target.value }))}
                style={{ width:28, height:20, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer", padding:0 }}/>
            </div>
          </div>
        )}
      </div>
      </div>{/* end scrollable */}

      <div style={{ display:"flex", gap:6, justifyContent:"flex-end", paddingTop:8 }}>
        <OptBtn onClick={handleCancel}>Cancel</OptBtn>
        <OptBtn accent onClick={handleCommit}>Apply</OptBtn>
      </div>
    </Modal>
  );
}
